/**
 * 背景画像の隠蔽率を計算するためのユーティリティ
 * SVGパターンがどれだけ背景を覆っているかをピクセル単位で計算します
 */

// 計算結果をキャッシュするためのオブジェクト
interface CoverageCache {
  svgHash: string;
  backgroundSrc: string;
  threshold: number;
  width: number;
  height: number;
  result: number;
  bgDataUrl: string;
  compositeDataUrl: string;
  timestamp: number;
}

// 最後の計算結果をキャッシュする
let lastCalculation: CoverageCache | null = null;

/**
 * 単純なハッシュ関数 - SVG文字列の特徴を抽出
 */
function simpleHash(str: string): string {
  // 長いSVG文字列の一部だけを使ってハッシュを生成
  const sampleSize = 1000;
  const samples = [
    str.substring(0, sampleSize),
    str.substring(
      Math.floor(str.length / 2),
      Math.floor(str.length / 2) + sampleSize
    ),
    str.substring(str.length - sampleSize),
  ];

  let hash = 0;
  const combined = samples.join('');

  for (let i = 0; i < combined.length; i++) {
    hash = (hash << 5) - hash + combined.charCodeAt(i);
    hash = hash & hash; // 32bit整数に変換
  }

  return hash.toString(16);
}

/**
 * SVGパターンと背景画像を合成した際の隠蔽率を計算します
 * @param svgString SVG文字列
 * @param backgroundImage 背景画像のURL
 * @param width 幅
 * @param height 高さ
 * @param threshold 閾値 - どれだけの色の変化を「変化あり」とみなすか
 * @returns 隠蔽率情報（隠蔽率の数値と画像データURL）
 */
export async function calculateCoverageWithBackground(
  svgString: string,
  backgroundImage: string,
  width: number,
  height: number,
  threshold = 1
): Promise<{
  coverage: number;
  bgDataUrl: string;
  compositeDataUrl: string;
  changed: boolean;
}> {
  // SVGのハッシュを計算
  const svgHash = simpleHash(svgString);

  // 前回の計算と同じパラメータであれば、キャッシュから結果を返す
  if (
    lastCalculation &&
    lastCalculation.svgHash === svgHash &&
    lastCalculation.backgroundSrc === backgroundImage &&
    lastCalculation.threshold === threshold &&
    lastCalculation.width === width &&
    lastCalculation.height === height
  ) {
    return {
      coverage: lastCalculation.result,
      bgDataUrl: lastCalculation.bgDataUrl,
      compositeDataUrl: lastCalculation.compositeDataUrl,
      changed: false,
    };
  }

  // 背景画像がない場合は計算不要
  if (!backgroundImage) {
    return {
      coverage: 0,
      bgDataUrl: '',
      compositeDataUrl: '',
      changed: false,
    };
  }

  // 小数点の問題を修正するため整数に変換
  const canvasWidth = Math.floor(width);
  const canvasHeight = Math.floor(height);

  // キャンバスを作成
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    console.error('キャンバスのコンテキストを取得できませんでした');
    return {
      coverage: 0,
      bgDataUrl: '',
      compositeDataUrl: '',
      changed: false,
    };
  }

  // 背景画像を読み込み
  const bgImg = new Image();

  return new Promise((resolve) => {
    bgImg.onload = () => {
      // 背景画像のみのキャンバス
      const bgCanvas = document.createElement('canvas');
      bgCanvas.width = canvasWidth;
      bgCanvas.height = canvasHeight;
      const bgCtx = bgCanvas.getContext('2d');

      if (!bgCtx) {
        console.error('背景キャンバスのコンテキストを取得できませんでした');
        resolve({
          coverage: 0,
          bgDataUrl: '',
          compositeDataUrl: '',
          changed: false,
        });
        return;
      }

      // 背景画像を描画
      bgCtx.drawImage(bgImg, 0, 0, canvasWidth, canvasHeight);

      // 背景画像のDataURLを保存
      const bgDataUrl = bgCanvas.toDataURL('image/jpeg', 0.7);

      // 背景のみのピクセルデータを取得
      const backgroundImageData = bgCtx.getImageData(
        0,
        0,
        canvasWidth,
        canvasHeight
      );
      const backgroundData = backgroundImageData.data;

      // 背景データの統計情報をログ
      const bgStats = analyzeImageData(backgroundData);
      console.log('背景画像の統計:', bgStats);

      // SVGを描画するための画像を作成
      const svgImg = new Image();

      svgImg.onload = () => {
        // 合成用の新しいキャンバスを作成
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = canvasWidth;
        compositeCanvas.height = canvasHeight;
        const compositeCtx = compositeCanvas.getContext('2d');

        if (!compositeCtx) {
          console.error('合成キャンバスのコンテキストを取得できませんでした');
          resolve({
            coverage: 0,
            bgDataUrl,
            compositeDataUrl: '',
            changed: false,
          });
          return;
        }

        // まず背景画像を描画
        compositeCtx.drawImage(bgImg, 0, 0, canvasWidth, canvasHeight);

        // SVGを背景画像の上に描画
        compositeCtx.drawImage(svgImg, 0, 0, canvasWidth, canvasHeight);

        // 合成画像のDataURLを保存
        const compositeDataUrl = compositeCanvas.toDataURL('image/jpeg', 0.7);

        // 合成後のピクセルデータを取得
        const compositeImageData = compositeCtx.getImageData(
          0,
          0,
          canvasWidth,
          canvasHeight
        );
        const compositeData = compositeImageData.data;

        // 合成データの統計情報をログ
        const compositeStats = analyzeImageData(compositeData);
        console.log('合成画像の統計:', compositeStats);

        // 変化したピクセル数をカウント
        let changedPixels = 0;
        let nonTransparentPixels = 0; // 背景画像の透明でないピクセル数

        // 色差の最大値と平均値を記録
        let maxDiff = 0;

        // 閾値を1.5倍にして差分を厳しくチェック（より大きな変化のみカウント）
        const stricterThreshold = threshold * 1.5;

        // RGBAのデータを4バイトずつ比較
        for (let i = 0; i < backgroundData.length; i += 4) {
          // アルファ値をチェック（透明な部分は計算対象外）
          const alpha = backgroundData[i + 3];
          if (alpha > 0) {
            nonTransparentPixels++;

            // コンポジット（SVGが描画された後）の色を取得
            const compositeR = compositeData[i];
            const compositeG = compositeData[i + 1];
            const compositeB = compositeData[i + 2];

            // 黒っぽい色かどうかを判定（RGB値が低い色のみカウント）
            const isBlackish =
              compositeR <= 50 &&
              compositeG <= 50 &&
              compositeB <= 50 &&
              compositeR + compositeG + compositeB < 150;

            // 黒っぽい色の場合のみ計算対象とする
            if (isBlackish) {
              // 各色チャンネルの差分を計算
              const rDiff = Math.abs(backgroundData[i] - compositeData[i]);
              const gDiff = Math.abs(
                backgroundData[i + 1] - compositeData[i + 1]
              );
              const bDiff = Math.abs(
                backgroundData[i + 2] - compositeData[i + 2]
              );

              // 最大色差を更新
              const maxChannelDiff = Math.max(rDiff, gDiff, bDiff);
              maxDiff = Math.max(maxDiff, maxChannelDiff);

              // 色の差異をチェック - すべてのチャンネルで閾値を超える必要がある
              // これにより、わずかな変化は無視される
              if (
                rDiff > stricterThreshold &&
                gDiff > threshold &&
                bDiff > threshold
              ) {
                changedPixels++;
              }
            }
          }
        }

        // 背景画像に透明でないピクセルがない場合の対応
        if (nonTransparentPixels === 0) {
          console.warn('背景画像に不透明なピクセルがありません');
          resolve({
            coverage: 0,
            bgDataUrl,
            compositeDataUrl,
            changed: false,
          });
          return;
        }

        // 隠蔽率を計算（変化したピクセル数 / 不透明な全ピクセル数）
        const coverage = (changedPixels / nonTransparentPixels) * 100;

        // 計算結果をキャッシュに保存
        lastCalculation = {
          svgHash,
          backgroundSrc: backgroundImage,
          threshold,
          width,
          height,
          result: coverage,
          bgDataUrl,
          compositeDataUrl,
          timestamp: Date.now(),
        };

        resolve({
          coverage,
          bgDataUrl,
          compositeDataUrl,
          changed: true,
        });
      };

      svgImg.onerror = (error) => {
        console.error('SVG画像の読み込みに失敗しました:', error);
        resolve({
          coverage: 0,
          bgDataUrl,
          compositeDataUrl: '',
          changed: false,
        });
      };

      // SVG文字列をData URLに変換して画像に設定
      try {
        svgImg.src =
          'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
      } catch (error) {
        console.error('SVG文字列の変換エラー:', error);
        resolve({
          coverage: 0,
          bgDataUrl,
          compositeDataUrl: '',
          changed: false,
        });
      }
    };

    bgImg.onerror = (error) => {
      console.error('背景画像の読み込みに失敗しました:', error);
      resolve({
        coverage: 0,
        bgDataUrl: '',
        compositeDataUrl: '',
        changed: false,
      });
    };

    // 背景画像を読み込み
    bgImg.src = backgroundImage;
  });
}

/**
 * 画像データの統計情報を計算する補助関数
 */
function analyzeImageData(data: Uint8ClampedArray) {
  const stats = {
    min: [255, 255, 255], // RGB最小値
    max: [0, 0, 0], // RGB最大値
    avg: [0, 0, 0], // RGB平均値
    nonZeroPixels: 0, // 完全な透明でないピクセル数
    totalPixels: data.length / 4,
  };

  let rSum = 0,
    gSum = 0,
    bSum = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a > 0) {
      stats.nonZeroPixels++;

      // 最小値の更新
      stats.min[0] = Math.min(stats.min[0], r);
      stats.min[1] = Math.min(stats.min[1], g);
      stats.min[2] = Math.min(stats.min[2], b);

      // 最大値の更新
      stats.max[0] = Math.max(stats.max[0], r);
      stats.max[1] = Math.max(stats.max[1], g);
      stats.max[2] = Math.max(stats.max[2], b);

      // 合計値の更新
      rSum += r;
      gSum += g;
      bSum += b;
    }
  }

  // 平均値の計算（非透明ピクセルのみ）
  if (stats.nonZeroPixels > 0) {
    stats.avg[0] = Math.round(rSum / stats.nonZeroPixels);
    stats.avg[1] = Math.round(gSum / stats.nonZeroPixels);
    stats.avg[2] = Math.round(bSum / stats.nonZeroPixels);
  }

  return stats;
}
